import { Outlet, useLocation } from "react-router-dom";
import { useContent, useFormState } from "hooks";
import Header from "components/header/Header";
import Footer from "components/footer/Footer";
import ProgressHeader from "components/progress-bar/ProgressHeader";
import ErrorBoundary from "components/error-boundary/ErrorBoundary";
import { FooterContentSchema, HeaderContentSchema } from "content/contentSchemas";
import { ReactNode, useEffect, useMemo } from "react";
import { toPageNameFromPathname } from "constants/pageNames";
import { getProgressConfig, getProgressStepFromPathname, type ProgressBarConfig } from "constants/progressBarConfig";
import { useProgressBar } from "context/ProgressBarContext";

const HeaderAndFooterSchema = HeaderContentSchema.merge(FooterContentSchema);

type LayoutWithHeaderAndFooterProps = {
    children: ReactNode;
    noMargins?: boolean;
    progressBarConfig?: ProgressBarConfig;
};

// The Outlet will render the element from the react-router route if it exists
// Optionally, render children so that we can also use this component in storybook - which has no context of routes
const LayoutWithHeaderAndFooter = ({ children, noMargins = false, progressBarConfig }: LayoutWithHeaderAndFooterProps) => {
    const { state } = useFormState();
    const { pathname } = useLocation();
    const pagePath = toPageNameFromPathname(pathname);

    const isApplicationRoute = pathname.includes("/application");
    const progressStep = getProgressStepFromPathname(pathname);

    const { progressConfig, setProgressConfig } = useProgressBar();

    const baseProgressBarConfig = useMemo(
        () => (isApplicationRoute && progressStep ? getProgressConfig(progressStep) : progressBarConfig),
        [isApplicationRoute, progressStep, progressBarConfig]
    );

    useEffect(() => {
        if (isApplicationRoute && baseProgressBarConfig) {
            setProgressConfig(baseProgressBarConfig);
        } else if (!isApplicationRoute) {
            setProgressConfig(null);
        }
    }, [baseProgressBarConfig, setProgressConfig, isApplicationRoute]);

    const dynamicProgressBarConfig = isApplicationRoute ? progressConfig || baseProgressBarConfig : null;

    const { result } = useContent({
        targetSchema: HeaderAndFooterSchema,
        applicationMode: state.applicationMode,
        pageName: pagePath,
        clubCode: state?.clubSpecificData?.clubCode ?? state?.campaign?.clubCode ?? undefined,
        state: state?.application?.state,
    });

    if (!result) {
        return null;
    }

    return (
        <main className="min-h-screen flex flex-col">
            {dynamicProgressBarConfig ? (
                <ProgressHeader
                    productName={dynamicProgressBarConfig.productName}
                    currentStep={dynamicProgressBarConfig.currentStep}
                    stepCompletion={dynamicProgressBarConfig.stepCompletion}
                    steps={dynamicProgressBarConfig.steps}
                />
            ) : (
                <Header content={result} />
            )}
            <div className={`flex-1 flex justify-center ${noMargins ? "" : "mx-auto w-full tablet:max-w-content"}`}>
                <ErrorBoundary resetKey={pathname}>
                    <Outlet />
                    {children}
                </ErrorBoundary>
            </div>
            <Footer />
        </main>
    );
};

export default LayoutWithHeaderAndFooter;
